import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router";
import { Markdown, type MarkdownComponents } from "@/components/chat/Markdown";
import { MARKDOWN_LINK_CLASS } from "@/components/chat/MarkdownMedia";
import { slugify } from "./parseArticle";

type WithNode<T extends keyof React.JSX.IntrinsicElements> = ComponentProps<T> & { node?: unknown };

function textOf(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textOf).join("");
  if (children && typeof children === "object" && "props" in children) {
    return textOf((children.props as { children?: ReactNode }).children);
  }
  return "";
}

/** In-app paths navigate with the router; anything else opens in a new tab. */
function HelpLink({ href, children, node: _node, ...rest }: Readonly<WithNode<"a">>) {
  if (href?.startsWith("/")) {
    return (
      <Link {...rest} to={href} className={MARKDOWN_LINK_CLASS}>
        {children}
      </Link>
    );
  }
  return (
    <a
      {...rest}
      href={href}
      className={MARKDOWN_LINK_CLASS}
      rel="noreferrer"
      target={href?.startsWith("#") ? undefined : "_blank"}
    >
      {children}
    </a>
  );
}

// Headings carry the same ids the search index uses, so `/help/<slug>#<id>` lands on them.
function H2({ children, node: _node, className: _c, ...rest }: Readonly<WithNode<"h2">>) {
  return (
    <h2
      {...rest}
      id={slugify(textOf(children))}
      className="mt-8 mb-3 scroll-mt-6 [font:var(--type-h3)] text-content-primary"
    >
      {children}
    </h2>
  );
}

function H3({ children, node: _node, className: _c, ...rest }: Readonly<WithNode<"h3">>) {
  return (
    <h3
      {...rest}
      id={slugify(textOf(children))}
      className="mt-6 mb-2 scroll-mt-6 [font:var(--type-body)] [font-weight:var(--weight-semibold)] text-content-primary"
    >
      {children}
    </h3>
  );
}

/** `> **Tip:** …` / `> **Note:** …` callouts: one subtle, neutral surface for all. */
function Callout({ children, node: _node, className: _c, ...rest }: Readonly<WithNode<"blockquote">>) {
  return (
    <blockquote
      {...rest}
      className="my-4 rounded-lg border border-border-subtle bg-surface-secondary px-4 py-3 [&>p]:my-0"
    >
      {children}
    </blockquote>
  );
}

const COMPONENTS: MarkdownComponents = { a: HelpLink, h2: H2, h3: H3, blockquote: Callout };

/** A help article body: the chat markdown renderer with router links, anchored headings and callouts. */
export function HelpMarkdown({ children }: Readonly<{ children: string }>) {
  return (
    <div className="[&_.chat-markdown]:[font:var(--type-body)]">
      <Markdown components={COMPONENTS}>{children}</Markdown>
    </div>
  );
}
